<template>
  <div class="edit-post-form">
    <div v-if="isLoadingSummaryPayload" class="edit-post-form__loading-overlay">
      <Loader class="edit-post-form__loader" />
    </div>
    <!-- Standard page header -->
    <div class="standard-page-header edit-post-form__header">
      <div class="edit-post-form__header-title-row">
        <h1 class="global-h1 standard-page-header__title edit-post-form__header-title">{{ postTypeTitle }} to</h1>
        <SocialPostNetworksRow
          class="edit-post-form__social-row"
          :post="props.postToEdit"
          :allCustomerSocialChannels="props.customerSocialChannels"
          :isEditable="!props.readonlyMode && !props.isSavingPost"
          :logoSizePx="30"
          @updatePostToAccounts="handleUpdatePostToAccounts"
          @addSocialAccount="handleAddSocialAccount"
        />
      </div>
    </div>
    <!-- / Standard page header -->

    <div v-if="isVaryByNetwork" class="edit-post-form__target-controls">
      <div class="edit-post-form__network-tabs-row">
        <div v-if="varyByNetworkTabChannels.length" class="edit-post-form__network-tabs">
          <button
            v-for="channel in varyByNetworkTabChannels"
            :key="channel"
            type="button"
            :class="['edit-post-form__network-tab', 'edit-post-form__network-tab--icon', { 'edit-post-form__network-tab--active': activeEditTarget === channel }]"
            @click="activeEditTarget = channel"
            :disabled="props.isSavingPost"
            :title="getSocialChannelLabel(channel)"
          >
            <img class="edit-post-form__network-tab-icon" :src="`/images/social-logo-${channel.toLowerCase()}.svg`" :alt="getSocialChannelLabel(channel)" />
            <span v-if="isChannelEdited(channel)" class="edit-post-form__network-tab-edited-dot" />
          </button>
        </div>
        <LinkWithIcon
          class="edit-post-form__vary-link edit-post-form__vary-link--tabs"
          color="gray"
          isDottedUnderline
          :isDisabled="props.readonlyMode || props.isSavingPost"
          @click="toggleVaryByNetwork"
        >
          <template #icon><IconEmbedded name="remove_3" :size="10" /></template>
          <span>Cancel per network</span>
        </LinkWithIcon>
      </div>
    </div>

    <div v-if="!isStoryPost" class="edit-post-form__section">
      <div class="edit-post-form__section-title-row">
        <h4 class="global-h4 edit-post-form__section-title">{{ captionLabelText }}</h4>
        <LinkWithIcon
          v-if="!isVaryByNetwork && selectedChannels.length > 1"
          class="edit-post-form__vary-link"
          color="gray"
          isDottedUnderline
          :isDisabled="props.readonlyMode || props.isSavingPost"
          @click="toggleVaryByNetwork"
        >
          <template #icon><IconEmbedded name="edit-pencil_2" :size="16" /></template>
          <span>Adjust per network</span>
        </LinkWithIcon>
      </div>
      <PostCaptionEditorPanel
        class="edit-post-form__section-content"
        v-model:caption="activeCaption"
        :customerId="props.customerId"
        :calendarId="props.postToEdit?.calendarId ?? ''"
        :postId="props.postToEdit?.id ?? ''"
        :assetIds="activeAssetIds"
        :aiEditorKey="`${props.postToEdit?.id ?? 'new'}-${activeEditTarget}`"
        :isDisabled="props.readonlyMode || props.isSavingPost"
        :isCopyDisabled="isRequestingDownload"
      />
    </div>

    <div class="edit-post-form__section" style="margin-top: 30px">
      <div class="edit-post-form__section-title-row">
        <h4 class="global-h4 edit-post-form__section-title">
          {{ activeEditTarget === "template" ? "Photos" : `${getSocialChannelLabel(activeEditTarget)} Photos` }}<span style="color: rgba(0, 0, 0, 0.5)"> </span>
        </h4>
        <TooltipWrapper text="Download all photos" :disabled="isRequestingDownload || props.isSavingPost">
          <template #hoverableElement>
            <LinkWithIcon color="gray" isNoUnderline :isDisabled="isRequestingDownload || props.isSavingPost" aria-label="Download photos" @click="_handleDownloadClick">
              <template #icon>
                <LoadingIcon v-if="isRequestingDownload || props.isSavingPost" :size="18" />
                <IconEmbedded v-else name="download_2-5" :size="18" />
              </template>
            </LinkWithIcon>
          </template>
        </TooltipWrapper>
      </div>
      <!-- Media thumbnails section -->
      <div class="media-thumbnails-section edit-post-form__section-content">
        <div
          :class="{
            'media-thumbnails-section__thumbnails-list-wrap': true,
            'media-thumbnails-section__thumbnails-list-wrap--with-left-curtain': isLeftCurtainVisible,
            'media-thumbnails-section__thumbnails-list-wrap--with-right-curtain': isRightCurtainVisible,
          }"
        >
          <VueDraggable
            ref="domRefThumbnailsListComponent"
            v-model="activeAssetIdsDraggable"
            class="media-thumbnails-section__thumbnails-list"
            :disabled="!isThumbnailReorderEnabled"
            :animation="150"
            ghost-class="media-thumbnails-section__thumbnail-wrap--sortable-ghost"
            draggable=".media-thumbnails-section__thumbnail-wrap--draggable"
            filter=".media-thumbnails-section__actions-dropdown"
            :delay="150"
            :delay-on-touch-only="true"
          >
            <div
              v-for="(assetId, assetIndex) in activeAssetIdsDraggable"
              :key="assetId"
              :class="{
                'media-thumbnails-section__thumbnail-wrap': true,
                'media-thumbnails-section__thumbnail-wrap--draggable': isThumbnailReorderEnabled,
              }"
            >
              <SrpFileThumbnail
                class="media-thumbnails-section__thumbnail"
                thumbSize="thumb-med"
                :fileName="assetId"
                borderRadius="0"
                :isFixedAspectRatio="false"
                :gallery-postfix="props.postToEdit.id"
                isEntireAreaClickable
              />
              <SrpDropdown class="media-thumbnails-section__actions-dropdown" :placement="'top-start'" :offset="8">
                <template #clickableElement>
                  <button type="button" class="media-thumbnails-section__actions-trigger" :aria-label="'Open menu for media ' + (assetIndex + 1)">
                    <IconEmbedded name="edit-pencil_2" :size="14" color="currentColor" />
                  </button>
                </template>
                <template #dropdown="{ close }">
                  <div class="media-thumbnails-section__actions-menu">
                    <button
                      type="button"
                      class="media-thumbnails-section__actions-menu-item"
                      :disabled="props.readonlyMode || props.isSavingPost"
                      @click="handleThumbnailMenuRemove(assetIndex, close)"
                    >
                      <IconEmbedded name="trashcan-alt_2" :size="16" color="black" />
                      <span>Remove</span>
                    </button>
                    <button
                      type="button"
                      class="media-thumbnails-section__actions-menu-item"
                      :disabled="props.readonlyMode || props.isSavingPost || assetIndex === 0"
                      @click="handleThumbnailMenuMove(assetIndex, 'left', close)"
                    >
                      <IconEmbedded name="arrow-left_2-5" :size="16" color="black" />
                      <span>Move left</span>
                    </button>
                    <button
                      type="button"
                      class="media-thumbnails-section__actions-menu-item"
                      :disabled="props.readonlyMode || props.isSavingPost || assetIndex === activeAssetIdsDraggable.length - 1"
                      @click="handleThumbnailMenuMove(assetIndex, 'right', close)"
                    >
                      <IconEmbedded name="arrow-right_2-5" :size="16" color="black" />
                      <span>Move right</span>
                    </button>
                    <button
                      v-if="isImageAssetId(assetId)"
                      type="button"
                      class="media-thumbnails-section__actions-menu-item"
                      :disabled="props.readonlyMode || props.isSavingPost"
                      @click="handleThumbnailMenuEdit(assetId, assetIndex, close)"
                    >
                      <IconEmbedded name="edit-pencil_2" :size="16" color="black" />
                      <span>Edit Image</span>
                    </button>
                  </div>
                </template>
              </SrpDropdown>
            </div>
            <div class="media-thumbnails-section__thumbnail-wrap media-thumbnails-section__thumbnail-wrap--add-more">
              <button type="button" class="media-thumbnails-section__add-more-tile" :disabled="isRequestingDownload || props.isSavingPost" @click="emit('clickEditImages', activeEditTarget)">
                <IconEmbedded v-if="!isSingleMediaPostType" name="plus_2-5" :size="25" color="currentColor" />
                <span>{{ isSingleMediaPostType ? "Change" : "Add" }}</span>
              </button>
            </div>
          </VueDraggable>
        </div>
      </div>
      <!-- / Media thumbnails section -->
      <NoteWithIcon v-if="isVaryByNetworkDifferenceNoteVisible" color="blue" size="small" class="edit-post-form__vary-differences-note">
        <template #text>
          <div v-for="(line, lineIndex) in varyByNetworkDifferenceNoteLines" :key="lineIndex" class="edit-post-form__vary-differences-line">
            <strong v-if="line.isBold">{{ line.text }}</strong>
            <template v-else>{{ line.text }}</template>
          </div>
        </template>
      </NoteWithIcon>
    </div>

    <div v-if="!props.sectionsToHide.includes(SocialPostEditAndShareFormSections.CreatorTag) && socialShareSummaryPayload?.adventureCreatorSummaries?.length" class="edit-post-form__section">
      <!--<h4 class="global-h4 edit-post-form__section-title">Creator tag</h4>-->

      <!-- Tags copying section -->
      <div class="tags-copying-section edit-post-form__section-content" v-for="creatorSummary in socialShareSummaryPayload.adventureCreatorSummaries" :key="creatorSummary.creatorId">
        <div class="tags-copying-section__left-side">
          <AvatarWithFallback class="tags-copying-section__avatar" :src="creatorSummary.profilePhoto" />
        </div>
        <div class="tags-copying-section__right-side">
          <h5 class="global-h5 tags-copying-section__title">
            {{ creatorSummary.displayName }}
          </h5>
          <SocialMediaLinksBar class="tags-copying-section__handles-row" show-copy-icon :isButtonHaveText="false" :socialFields="creatorSummary.socialFields" />
        </div>
      </div>
      <!-- / Tags copying section -->
    </div>

    <div v-if="false" class="edit-post-form__section">
      <h4 class="global-h4 edit-post-form__section-title">Locations</h4>
      <!-- Tags copying section -->
      <div class="tags-copying-section edit-post-form__section-content">
        <div class="tags-copying-section__left-side">
          <IconEmbedded class="tags-copying-section__location-icon" name="map-pin_1-5" :size="45" />
        </div>
        <div class="tags-copying-section__right-side">
          <h5 class="global-h5 tags-copying-section__title">McDonalds</h5>
          <SocialMediaLinksBar
            class="tags-copying-section__handles-row"
            show-copy-icon
            :isButtonHaveText="false"
            :socialFields="{
              twitterHandle: 'kotofeyweb',
              instagramHandle: '',
              facebookURL: '',
              youtubeURL: 'https://youtube.com/kotofey_web',
              tikTokHandle: '',
            }"
          />
        </div>
      </div>
      <!-- / Tags copying section -->
    </div>

    <SrpModal v-model:isVisible="isImageEditorModalVisible" width="90vw" size="large" :isClosable="false" :isCloseButtonVisible="true">
      <template #header>
        <h2 class="global-h2">Edit image</h2>
      </template>
      <template #content>
        <div v-if="pinturaErrorMessage" class="pintura-modal-error">{{ pinturaErrorMessage }}</div>
        <InlineImageEditor v-if="selectedImageEditSrc" ref="inlineImageEditorRef" :src="selectedImageEditSrc" :is-busy="isImageEditorBusy" :disabled="isImageEditorBusy" />
      </template>
      <template #footer="{ closeModal }">
        <div class="pintura-modal-footer">
          <SrpButton color="gray" fill="outlined" :isDisabled="isImageEditorBusy || props.isSavingPost" @click="closeImageEditorModal(closeModal)">Cancel</SrpButton>
          <SrpButton :isDisabled="isImageEditorBusy || props.isSavingPost || props.readonlyMode || !selectedImageEditSrc" @click="handleSaveEditedImage(closeModal)">
            <template #icon>
              <LoadingIcon v-if="isImageEditorBusy || props.isSavingPost" :size="18" />
              <IconEmbedded v-else name="save_2-5" :size="18" />
            </template>
            Save
          </SrpButton>
        </div>
      </template>
    </SrpModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { VueDraggable } from "vue-draggable-plus";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import { MetricSender } from "@helpers/MetricSender";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import FileDownload from "@logic/FileDownload";
import FileUtils from "@logic/FileUtils";
import ImageUploader from "@logic/ImageUploader";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import AvatarWithFallback from "@components/AvatarWithFallback.vue";
import SocialMediaLinksBar from "@components/SocialMediaLinksBar.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import Loader from "@components/Loader/Loader.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import InlineImageEditor from "@components/image-editors/InlineImageEditor.vue";
import SocialPostNetworksRow from "@components/SocialPostPreview/SocialPostNetworksRow.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import PostCaptionEditorPanel from "./PostCaptionEditorPanel.vue";

// Composables
import useScrollCurtains from "@composables/useScrollCurtains";

// Types
import { SocialMediaPostData, SocialPostPublishType, SocialShareSummaryPayload } from "@contracts/shareStop";
import { getSocialChannelLabel, SocialChannels, sortSocialChannelsForDisplay } from "@contracts/socialNetworks";
import type { SocialChannelData } from "@contracts/socialMediaSettings";
import { SocialPostEditAndShareFormSections } from "./index.vue";
import {
  ChannelEditTarget,
  getAssetIdsForTarget as getAssetIdsForEditTarget,
  getCaptionForTarget as getCaptionForEditTarget,
  setCaptionForTarget as setCaptionForEditTarget,
  setAssetIdsForTarget as setAssetIdsForEditTarget,
  isChannelEdited as isChannelEditedFromOverrides,
  shouldUseChannelOverrides,
  getNetworkContentDifferenceSummary,
} from "@logic/SocialPostChannelOverrides";
import { getMaxMediaCount } from "@logic/AtlasSchedulingValidation";

// Global variables & injections
const remoteLogger = getGlobalRemoteLogger();
const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;
// CDN full URLs cause CORS issues with Pintura (same as vue-advanced-cropper in ImageCropper.vue); use blob base + uncompressed.
const EditImageBaseUriPath = `${globalThis.Bootstrap.Config.contentBlobBaseUri}/cms/images/uncompressed/`;

const props = withDefaults(
  defineProps<{
    postToEdit: SocialMediaPostData | null;
    customerId: string;
    isLoadingSummaryPayload: boolean;
    socialShareSummaryPayload: SocialShareSummaryPayload | null;
    /**
     * Persists the modal draft to the API. Pass mergeToParent: true to also update the calendar/list
     * (footer Save, Pintura). Omit or pass false when the user may still Cancel the modal (e.g. add
     * social account) so the parent keeps showing the pre-edit post until an explicit Save.
     * NOTE: Edit Image DOES save any draft updates (since it calls a full post save to persist)
     * but currently changing social accounts ONLY saves the accounts and not the draft changes to caption or photos (since it's a separate API call)
     *  In the 2nd scenario the user needs to click Save to persist them.
     */
    savePostCallback: (options?: { mergeToParent?: boolean }) => Promise<SocialMediaPostData | undefined>;
    isSavingPost: boolean;
    customerSocialChannels?: Array<SocialChannelData>;
    readonlyMode?: boolean;
    sectionsToHide?: Array<SocialPostEditAndShareFormSections>;
    /** Parent keeps last target when opening photo selection; used to restore tab after form remounts. */
    restoreEditTarget?: ChannelEditTarget;
  }>(),
  {
    postToEdit: null,
    customerId: "",
    isLoadingSummaryPayload: false,
    socialShareSummaryPayload: null,
    savePostCallback: async (): Promise<SocialMediaPostData | undefined> => {
      throw new Error("savePostCallback is not set");
    },
    isSavingPost: false,
    customerSocialChannels: () => [],
    readonlyMode: false,
    sectionsToHide: () => [],
    restoreEditTarget: "template",
  }
);

const emit = defineEmits<{
  (e: "clickEditImages", target: "template" | SocialChannels): void;
  (e: "requestDownload", value: boolean): void;
  (e: "addSocialAccount"): void;
}>();

type EditTarget = ChannelEditTarget;
const activeEditTarget = ref<EditTarget>("template");
const isVaryByNetwork = ref<boolean>(false);
const editablePost = computed(() => props.postToEdit);
const selectedChannels = computed(() => editablePost.value?.postToAccounts ?? []);
const varyByNetworkTabChannels = computed(() => sortSocialChannelsForDisplay(selectedChannels.value));

watch(
  () => props.postToEdit?.id,
  () => {
    const post = props.postToEdit;
    isVaryByNetwork.value = shouldUseChannelOverrides(post);
    if (isVaryByNetwork.value && selectedChannels.value.length) {
      const channels = selectedChannels.value;
      const r = props.restoreEditTarget;
      activeEditTarget.value = r !== "template" && channels.includes(r) ? r : varyByNetworkTabChannels.value[0];
      return;
    }
    activeEditTarget.value = "template";
  },
  { immediate: true }
);

watch(
  () => selectedChannels.value,
  channels => {
    if (!isVaryByNetwork.value) {
      if (activeEditTarget.value === "template" || channels.includes(activeEditTarget.value)) return;
      activeEditTarget.value = "template";
      return;
    }
    if (!channels.length) {
      activeEditTarget.value = "template";
      return;
    }
    if (activeEditTarget.value !== "template" && channels.includes(activeEditTarget.value)) return;
    activeEditTarget.value = varyByNetworkTabChannels.value[0];
  }
);

const postTypeTitle = computed(() => editablePost.value?.postType ?? "Post");
const isStoryPost = computed(() => editablePost.value?.postType === SocialPostPublishType.Story);
const isSingleMediaPostType = computed(() => getMaxMediaCount(editablePost.value?.postType ?? SocialPostPublishType.Post) === 1);
const captionLabelText = computed(() => {
  if (!isVaryByNetwork.value) return "Caption";
  return activeEditTarget.value === "template" ? "Caption" : `${getSocialChannelLabel(activeEditTarget.value)} Caption`;
});
const activeAssetIds = computed(() => getAssetIdsForEditTarget(editablePost.value, activeEditTarget.value));

const isThumbnailReorderEnabled = computed(() => !props.readonlyMode && !props.isSavingPost);

const activeAssetIdsDraggable = computed({
  get: () => activeAssetIds.value,
  set: (next: string[]) => setAssetIdsForEditTarget(editablePost.value, activeEditTarget.value, next),
});

const activeCaption = computed({
  get: () => getCaptionForEditTarget(editablePost.value, activeEditTarget.value),
  set: value => setCaptionForEditTarget(editablePost.value, activeEditTarget.value, value ?? ""),
});

const isVaryByNetworkDifferenceNoteVisible = computed(() => isVaryByNetwork.value && selectedChannels.value.length > 1);
const networkContentDifferenceSummary = computed(() => {
  const post = editablePost.value;
  const channels = selectedChannels.value;
  void activeCaption.value;
  void activeAssetIds.value;
  return getNetworkContentDifferenceSummary(post, channels);
});
type VaryByNetworkDifferenceNoteLine = { text: string; isBold: boolean };

const varyByNetworkDifferenceNoteLines = computed((): Array<VaryByNetworkDifferenceNoteLine> => {
  const { captionDiffers, photosDiffer, hasAnyDifference } = networkContentDifferenceSummary.value;
  if (isStoryPost.value) {
    if (!photosDiffer) return [{ text: "No differences in Photos", isBold: false }];
    return [{ text: "Photos differ by network", isBold: true }];
  }
  if (!hasAnyDifference) return [{ text: "No differences in Caption or Photos", isBold: false }];
  return [
    {
      text: captionDiffers ? "Caption differs by network" : "Caption is the same",
      isBold: captionDiffers,
    },
    {
      text: photosDiffer ? "Photos differ by network" : "Photos are the same",
      isBold: photosDiffer,
    },
  ];
});

function isChannelEdited(channel: SocialChannels): boolean {
  return isChannelEditedFromOverrides(editablePost.value, channel);
}

function toggleVaryByNetwork() {
  const post = editablePost.value;
  if (!isVaryByNetwork.value) {
    if (post) post.useChannelOverrides = true;
    isVaryByNetwork.value = true;
    if (selectedChannels.value.length) activeEditTarget.value = varyByNetworkTabChannels.value[0];
    return;
  }
  if (post) {
    post.useChannelOverrides = false;
    post.channelOverrides = null;
  }
  isVaryByNetwork.value = false;
  activeEditTarget.value = "template";
}

function handleUpdatePostToAccounts(selectedSocialChannels: Array<SocialChannels>) {
  const post = editablePost.value;
  if (!post) return;
  post.postToAccounts = selectedSocialChannels;
}

async function handleAddSocialAccount() {
  if (!props.postToEdit || props.readonlyMode) {
    emit("addSocialAccount");
    return;
  }
  // Persist draft only; do not merge to parent until footer Save (user may still Cancel).
  await props.savePostCallback();
  emit("addSocialAccount");
}

// Thumbnails list scroll curtains ============================================
const domRefThumbnailsListComponent = ref<{ $el?: HTMLElement } | null>(null);
const domRefThumbnailsList = ref<HTMLElement | null>(null);
watch(
  domRefThumbnailsListComponent,
  component => {
    domRefThumbnailsList.value = component?.$el ?? null;
  },
  { flush: "post", immediate: true }
);
const { isLeft: isLeftCurtainVisible, isRight: isRightCurtainVisible } = useScrollCurtains(domRefThumbnailsList);

// Request download ===========================================================
const isRequestingDownload = ref<boolean>(false);

async function _handleDownloadClick() {
  if (!props.postToEdit) return;
  // await props.savePostCallback();
  await requestDownload();
  MetricSender.sendMetric("Cust-SocialPost-Download", { socialPostId: props.postToEdit.id });
}

async function requestDownload() {
  if (!props.postToEdit) return;
  isRequestingDownload.value = true;
  emit("requestDownload", true);

  const downloadId = await ShareStopSummaryRepo.requestDownloadId(props.customerId, props.postToEdit.id);

  isRequestingDownload.value = false;
  emit("requestDownload", false);

  FileDownload.downloadFileByURI(`${contentBaseUri}/${downloadId}`);
}

// Pintura super-user POC (modal + per-thumbnail overlay) ======================
const isImageEditorModalVisible = ref(false);
const isImageEditorBusy = ref(false);
const pinturaErrorMessage = ref<string | null>(null);
const selectedAssetId = ref<string | null>(null);
const selectedAssetIndex = ref<number>(-1);
const inlineImageEditorRef = ref<{ processImage: () => Promise<{ dest?: File } | null> } | null>(null);

function isImageAssetId(assetId: string): boolean {
  return !FileUtils.isVideoFileType(assetId);
}

const selectedImageEditSrc = computed(() => {
  if (!isImageEditorModalVisible.value || !selectedAssetId.value) return "";
  if (!isImageAssetId(selectedAssetId.value)) return "";
  return `${EditImageBaseUriPath}${selectedAssetId.value}`;
});

function openImageEditorModal(assetId: string, assetIndex: number) {
  if (props.readonlyMode) return;
  pinturaErrorMessage.value = null;
  selectedAssetId.value = assetId;
  selectedAssetIndex.value = assetIndex;
  isImageEditorModalVisible.value = true;
}

function handleThumbnailMenuEdit(assetId: string, assetIndex: number, closeDropdown?: () => void) {
  if (props.readonlyMode || props.isSavingPost) return;
  closeDropdown?.();
  openImageEditorModal(assetId, assetIndex);
}

function handleThumbnailMenuMove(assetIndex: number, direction: "left" | "right", closeDropdown?: () => void) {
  if (props.readonlyMode || props.isSavingPost || assetIndex < 0 || assetIndex >= activeAssetIds.value.length) return;
  const targetIndex = direction === "left" ? assetIndex - 1 : assetIndex + 1;
  if (targetIndex < 0 || targetIndex >= activeAssetIds.value.length) return;
  const next = [...activeAssetIds.value];
  const [movedAssetId] = next.splice(assetIndex, 1);
  next.splice(targetIndex, 0, movedAssetId);
  setAssetIdsForEditTarget(editablePost.value, activeEditTarget.value, next);
  closeDropdown?.();
}

function handleThumbnailMenuRemove(assetIndex: number, closeDropdown?: () => void) {
  if (props.readonlyMode || props.isSavingPost || assetIndex < 0 || assetIndex >= activeAssetIds.value.length) return;
  const next = [...activeAssetIds.value];
  next.splice(assetIndex, 1);
  setAssetIdsForEditTarget(editablePost.value, activeEditTarget.value, next);
  closeDropdown?.();
}

function closeImageEditorModal(closeModal?: (isClickOnBgLayer?: boolean, isForce?: boolean) => void) {
  pinturaErrorMessage.value = null;
  selectedAssetId.value = null;
  selectedAssetIndex.value = -1;
  isImageEditorModalVisible.value = false;
  closeModal?.(false, true);
}

async function handleSaveEditedImage(closeModal?: (isClickOnBgLayer?: boolean, isForce?: boolean) => void) {
  if (!activeAssetIds.value.length || selectedAssetIndex.value < 0 || !selectedAssetId.value || !props.postToEdit) return;
  pinturaErrorMessage.value = null;
  isImageEditorBusy.value = true;
  try {
    const result = await inlineImageEditorRef.value?.processImage();
    const dest = result?.dest;
    if (!dest) {
      pinturaErrorMessage.value = "No edited image produced. Apply your changes in the editor, then try Save again.";
      return;
    }
    const mime = dest instanceof Blob ? dest.type : "";
    const ext = mime.includes("png") ? "png" : "jpg";
    const clientFileName = `edited-${props.postToEdit.id}-${Date.now()}.${ext}`;
    // Save it back to the server!
    const editMetadata = {
      isEditedVersion: true,
      editedFromAssetId: selectedAssetId.value,
    };
    const newId = await ImageUploader.uploadItineraryImageBlob(dest, true, clientFileName, "social", undefined, editMetadata, props.customerId);
    if (!newId) throw new Error("Upload did not return an asset id");
    // Update the post!
    const next = [...activeAssetIds.value];
    next[selectedAssetIndex.value] = newId;
    setAssetIdsForEditTarget(editablePost.value, activeEditTarget.value, next);
    await props.savePostCallback({ mergeToParent: true });
    closeImageEditorModal(closeModal);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    remoteLogger.error(`Pintura save failed: postId=${props.postToEdit?.id ?? "?"} assetIndex=${selectedAssetIndex.value} assetId=${selectedAssetId.value ?? "?"} ${detail}`);
    pinturaErrorMessage.value = err instanceof Error ? err.message : "Could not save edited image";
  } finally {
    isImageEditorBusy.value = false;
  }
}
</script>

<style scoped lang="scss">
// Media thumbnails section ===================================================
.media-thumbnails-section {
  &__thumbnails-list-wrap {
    position: relative;
    z-index: 0;

    &::before,
    &::after {
      content: "";
      width: 30px;
      height: calc(100% - 5px);
      position: absolute;
      top: 0;
      z-index: 3;
      opacity: 0;
      background: rgba(255, 0, 0, 0.3);
      transition: opacity 0.07s ease-in-out;
      pointer-events: none;
    }

    &::before {
      left: 0;
      background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
    }
    &::after {
      right: 0;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
    }

    &--with-left-curtain {
      &::before {
        opacity: 1;
      }
    }
    &--with-right-curtain {
      &::after {
        opacity: 1;
      }
    }
  }

  &__thumbnails-list {
    gap: 10px;
    padding: 0;
    margin: 0 0 12px;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    position: relative;
    overflow: visible;
  }

  &__thumbnail-wrap {
    flex-shrink: 0;
    position: relative;

    &--draggable {
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }

    &--sortable-ghost {
      background: rgba(180, 180, 180, 1);
      border-radius: 4px;

      * {
        opacity: 0;
      }
    }

    &--add-more {
      align-self: stretch;
    }
  }

  &__thumbnail {
    height: 120px;
    /* :deep(.file-thumbnail__bg-image) {
      width: auto !important;
    }*/
  }

  &__add-more-tile {
    gap: 6px;
    width: 92px;
    height: 120px;
    padding: 0 8px;
    border: 1px dashed rgba(51, 51, 51, 0.45);
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: rgba(51, 51, 51, 0.78);
    background: rgba(255, 255, 255, 1);

    cursor: pointer;
    transition:
      border-color 0.07s ease-in-out,
      background-color 0.07s ease-in-out,
      color 0.07s ease-in-out;

    &:hover:not(:disabled) {
      border-color: rgba(17, 134, 137, 0.9);
      color: rgba(17, 134, 137, 1);
      background: rgba(17, 134, 137, 0.06);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__actions-dropdown {
    position: absolute;
    right: -6px;
    top: -4px;
    z-index: 12;
  }

  &__actions-trigger {
    width: 25px;
    height: 25px;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    color: rgba(255, 255, 255, 1);
    background: rgba(41, 34, 52, 0.82);
    border: none;
    border-radius: 999px;
    box-shadow: 0 2px 7px rgba(0, 0, 0, 0.35);

    &:hover:not(:disabled) {
      background: rgba(33, 27, 43, 1);
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__actions-menu {
    min-width: 150px;
    padding: 6px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: #fff;
  }

  &__actions-menu-item {
    gap: 8px;
    width: 100%;
    padding: 7px 8px;
    border: none;
    border-radius: 6px;
    display: flex;
    align-items: center;
    text-align: left;
    color: rgba(0, 0, 0, 0.78);
    background: transparent;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.06);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--danger {
      color: rgba(149, 32, 32, 1);
    }
  }
}

// Tags copying section =======================================================
.tags-copying-section {
  padding: 14px 12px 14px;
  border-radius: 6px;
  display: flex;
  background: rgba(0, 0, 0, 0.05);

  &__left-side {
    display: flex;
    align-items: center;
  }

  &__avatar {
    width: 42px !important;
    margin-right: 10px;
  }

  &__location-icon {
    margin-right: 8px;
  }

  &__right-side {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__title {
    margin-bottom: 5px;
    font-weight: 500;
  }

  &__handles-row {
    padding-left: 6px;
    display: flex;

    :deep(.social-links__in-wrap) {
      justify-content: flex-start;
    }
  }
}

// Edit post form =============================================================
.edit-post-form {
  position: relative;
  z-index: 0;

  &__loading-overlay {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    z-index: 1;
    background: rgba(255, 255, 255, 0.7);
  }

  &__loader {
    position: fixed;
    inset: calc(50dvh - 100px) auto auto calc(50% - 15px);
  }

  &__header {
    padding-bottom: 8px;
    margin-bottom: 20px;
  }

  &__header-title-row {
    gap: 10px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__header-title {
    margin-bottom: 3px !important;
  }

  &__social-row {
    display: inline-flex;
    // margin-bottom: 5px;
  }

  &__target-controls {
    margin-bottom: 18px;
  }

  &__network-tabs-row {
    gap: 12px;
    margin-top: 12px;
    display: flex;
    align-items: flex-end;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  }

  &__network-tabs {
    gap: 8px;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    flex: 1;
  }

  &__network-tab {
    gap: 8px;
    padding: 8px 12px 9px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-bottom: none;
    border-radius: 7px 7px 0 0;
    display: inline-flex;
    align-items: center;
    background: rgba(0, 0, 0, 0.08);
    cursor: pointer;
    position: relative;
    top: 1px;

    &--active {
      border-color: rgba(0, 0, 0, 0.35);
      font-weight: 700;
      background: white;
    }

    &--icon {
      width: 38px;
      height: 36px;
      padding: 0;
      justify-content: center;
    }
  }

  &__network-tab-icon {
    width: 18px;
    height: 18px;
  }

  &__network-tab-edited-dot {
    width: 8px;
    height: 8px;
    border-radius: 100px;
    position: absolute;
    top: 4px;
    right: 4px;
    background: #233f78;
  }

  &__vary-link {
    white-space: nowrap;

    &--tabs {
      margin-left: auto;
      margin-bottom: 8px;
    }
  }

  &__vary-button {
    white-space: nowrap;

    &--caption {
      margin-left: 0;
    }

    &--tabs {
      margin-left: auto;
    }
  }

  &__section {
    // padding-bottom: 25px;
    margin-bottom: 0px;
    position: relative;

    &::after {
      content: "";
      width: 100%;
      height: 0;
      // border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
      position: absolute;
      inset: auto auto 0 0;
    }

    &:last-child {
      padding-bottom: 0;
      margin-bottom: 0;

      &:after {
        display: none;
      }
    }
  }

  &__section-title-row {
    gap: 12px;
    margin-bottom: 7px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;

    .edit-post-form__section-title {
      margin-bottom: 0;
    }
  }

  &__section-title {
    margin-bottom: 7px;
  }

  &__section-content {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__vary-differences-note {
    margin-top: 14px;
  }

  &__vary-differences-line + &__vary-differences-line {
    margin-top: 4px;
  }
}

.pintura-modal-error {
  margin-bottom: 12px;
  color: #c00;
  font-size: 14px;
}

.pintura-modal-footer {
  gap: 12px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}
</style>
